import { z } from 'zod';
/**
 * Datenmodell eines Wertermittlungsprotokolls. Wird an JEDER API-Grenze
 * validiert (OWASP A03); Längen-/Wertebereiche begrenzen zugleich die
 * Payload-Größe. Der Server rechnet Summen aus diesen Rohdaten selbst nach.
 */
const text = (max) => z.string().trim().max(max);
const geld = z.number().min(0).max(1_000_000).finite();
const prozent = z.number().min(0).max(100);
export const fotoRefSchema = z.object({
    id: z.string().uuid(),
    caption: text(200).default(''),
});
export const bauteilSchema = z.object({
    id: z.string().uuid(),
    typ: z.enum(['Gartenlaube', 'Überdachter Freisitz', 'Anbau', 'Nebengebäude']),
    bk: z.number().int().min(1).max(6),
    rw: geld,
    flaeche: z.number().min(0).max(10_000).nullable(),
    baujahr: z.number().int().min(1900).max(2100).nullable(),
    zustand: z.number().int().min(0).max(4),
    ausfuehrung: text(2000).default(''),
    fotos: z.array(fotoRefSchema).max(20).default([]),
});
export const positionSchema = z.object({
    id: z.string().uuid(),
    name: text(200),
    einheit: text(20),
    preis: geld,
    menge: z.number().min(0).max(100_000),
    minderung: prozent.default(0),
    krit: text(500).default(''),
    g: z.enum(['zg', 'st', 'ra']).nullable().default(null),
    fotos: z.array(fotoRefSchema).max(20).default([]),
});
export const unterschriftSchema = z.object({
    rolle: text(80),
    name: text(120).default(''),
    funktion: text(120).default(''),
    img: z
        .string()
        .regex(/^data:image\/png;base64,[A-Za-z0-9+/=]+$/)
        .max(200_000)
        .nullable()
        .default(null),
});
export const protokollDataSchema = z.object({
    grund: z.object({
        anlage: text(200).default(''),
        parzelle: text(50).default(''),
        flaeche: z.number().min(0).max(100_000).nullable().default(null),
        datum: z
            .string()
            .regex(/^\d{4}-\d{2}-\d{2}$/)
            .nullable()
            .default(null),
        paechter: text(200).default(''),
        strasse: text(200).default(''),
        ort: text(200).default(''),
        anwesend: text(500).default(''),
    }),
    bauteile: z.array(bauteilSchema).max(20).default([]),
    pos: z.object({
        neben: z.array(positionSchema).max(200).default([]),
        kulturen: z.array(positionSchema).max(200).default([]),
        zier: z.array(positionSchema).max(200).default([]),
    }),
    pflege: z.object({
        kulturen: prozent.default(0),
        zier: prozent.default(0),
    }),
    nb: z
        .array(z.object({ text: text(300), grund: text(300).default('') }))
        .max(50)
        .default([]),
    abz: z
        .array(z.object({ text: text(300), betrag: geld }))
        .max(50)
        .default([]),
    gemeinschaft: geld.default(0),
    auflagen: text(4000).default(''),
    /** Übersichtsfotos des Gartens (Gesamteindruck) */
    fotosAllgemein: z.array(fotoRefSchema).max(30).default([]),
    sigs: z.array(unterschriftSchema).max(6).default([]),
});
/** Leeres Protokoll mit Standard-Laube und Unterschriftenfeldern */
export function leeresProtokoll(uuid) {
    return protokollDataSchema.parse({
        grund: {},
        bauteile: [
            {
                id: uuid(),
                typ: 'Gartenlaube',
                bk: 3,
                rw: 340,
                flaeche: null,
                baujahr: null,
                zustand: 1,
            },
        ],
        pos: {},
        pflege: {},
        sigs: [
            { rolle: 'Wertermittler/in 1', funktion: 'Vorsitz Bewertungskommission' },
            { rolle: 'Wertermittler/in 2', funktion: 'Beisitzer/in' },
            { rolle: 'Wertermittler/in 3', funktion: 'Beisitzer/in' },
            { rolle: 'Abgebende/r Pächter/in (optional)', funktion: 'Pächter/in' },
        ],
    });
}
/* ── API-Randschemas ── */
export const loginSchema = z.object({
    email: z.string().trim().toLowerCase().email().max(200),
    password: z.string().min(1).max(200),
});
export const protokollUpdateSchema = z.object({
    data: protokollDataSchema,
});
export const rollen = ['verband', 'vorstand', 'wertermittler'];
//# sourceMappingURL=schemas.js.map